import type { Diagnostic, Expr, Program, ScalarType, Stmt, ValueType } from './ir.js';
import {
  arrayOf,
  elemOf,
  isArray,
  isTextual,
  rankOf,
  stmtExprs,
  walkExpr,
  walkStmts,
} from './ir.js';

const isNumeric = (t: ValueType) => t === 'int' || t === 'double';

/**
 * A value put into a list place is itself a plain value; a nested list is not supported. Lists
 * hold whole texts, so a character put into one is a text of one character.
 */
const scalarOf = (t: ValueType): ScalarType => (t === 'char' ? 'string' : elemOf(t));

/** A one-character text written in the program ("a") can stand wherever a character is expected. */
export const isCharLiteral = (e: Expr): boolean => e.k === 'text' && [...e.value].length === 1;

/**
 * Static types, C++-style: every variable has one type for the whole program.
 * int by default; double once anything fractional flows in; string for text; char for one
 * character of a text, widened to string once a whole text flows in; bool for logic.
 * A variable fed incompatible values (a number and a text) is a `type-conflict`.
 */
class TypeChecker {
  env = new Map<string, ValueType>();
  diagnostics: Diagnostic[] = [];
  collect = false;

  private report(d: Diagnostic) {
    if (this.collect) this.diagnostics.push(d);
  }

  private assign(name: string, t: ValueType, blockId: string) {
    const cur = this.env.get(name);
    if (cur === undefined || cur === t) {
      this.env.set(name, t);
    } else if (isNumeric(cur) && isNumeric(t)) {
      this.env.set(name, 'double');
    } else if (isTextual(cur) && isTextual(t)) {
      this.env.set(name, 'string');
    } else if (isArray(cur) && isArray(t) && rankOf(cur) === rankOf(t)) {
      // A list of whole numbers that also receives fractions becomes a list of decimals.
      const l = elemOf(cur);
      const r = elemOf(t);
      if (l === r) return;
      if (isNumeric(l) && isNumeric(r)) this.env.set(name, arrayOf('double', rankOf(t) as 1 | 2));
      else this.report({ code: 'type-conflict', blockId, params: { name, was: cur, now: t } });
    } else {
      this.report({ code: 'type-conflict', blockId, params: { name, was: cur, now: t } });
    }
  }

  private expectNumeric(e: Expr, owner: string): ValueType {
    const t = this.expr(e);
    if (!isNumeric(t)) this.report({ code: 'needs-number', blockId: owner });
    return t;
  }

  /** A single character: `s[i]`, a code turned into a character, or a one-letter text "a". */
  private expectChar(e: Expr, owner: string) {
    const t = this.expr(e);
    if (t !== 'char' && !isCharLiteral(e) && e.k !== 'missing') {
      this.report({ code: 'needs-char', blockId: owner });
    }
  }

  private expectText(e: Expr, owner: string) {
    if (this.expr(e) !== 'string' && e.k !== 'missing') {
      this.report({ code: 'needs-text', blockId: owner });
    }
  }

  private expectBool(e: Expr, owner: string) {
    if (this.expr(e) !== 'bool' && e.k !== 'missing') {
      this.report({ code: 'needs-bool', blockId: owner });
    }
  }

  expr(e: Expr): ValueType {
    switch (e.k) {
      case 'num':
        return Number.isInteger(e.value) ? 'int' : 'double';
      case 'text':
        return e.char ? 'char' : 'string';
      case 'bool':
        return 'bool';
      case 'var':
        return this.env.get(e.name) ?? 'int';
      case 'read':
        return e.as === 'text' ? 'string' : e.as;
      case 'arith': {
        const l = this.expectNumeric(e.left, e.id);
        const r = this.expectNumeric(e.right, e.id);
        if (e.op === '/') return 'double';
        if (e.op === 'idiv') return 'int';
        if (e.op === 'mod') {
          if (l === 'double' || r === 'double') {
            this.report({ code: 'needs-whole-number', blockId: e.id });
          }
          return 'int';
        }
        return l === 'double' || r === 'double' ? 'double' : 'int';
      }
      case 'compare': {
        const l = this.expr(e.left);
        const r = this.expr(e.right);
        // Characters compare with characters and with texts, in dictionary (ASCII) order.
        const ok =
          (isNumeric(l) && isNumeric(r)) ||
          (isTextual(l) && isTextual(r)) ||
          (l === r && (e.op === '==' || e.op === '!='));
        if (!ok && e.left.k !== 'missing' && e.right.k !== 'missing') {
          this.report({ code: 'compare-mismatch', blockId: e.id });
        }
        return 'bool';
      }
      case 'logic':
        this.expectBool(e.left, e.id);
        this.expectBool(e.right, e.id);
        return 'bool';
      case 'not':
        this.expectBool(e.arg, e.id);
        return 'bool';
      case 'join':
        this.expr(e.left);
        this.expr(e.right);
        return 'string';
      case 'length':
        if (this.expr(e.arg) !== 'string') this.report({ code: 'needs-text', blockId: e.id });
        return 'int';
      case 'code':
        this.expectChar(e.arg, e.id);
        return 'int';
      case 'chr': {
        const t = this.expr(e.arg);
        if (t === 'double') this.report({ code: 'needs-whole-number', blockId: e.id });
        else if (t !== 'int' && e.arg.k !== 'missing')
          this.report({ code: 'needs-number', blockId: e.id });
        return 'char';
      }
      case 'case':
        this.expectChar(e.arg, e.id);
        return 'char';
      case 'find': {
        const needle = this.expr(e.needle);
        if (!isTextual(needle) && e.needle.k !== 'missing') {
          this.report({ code: 'needs-text', blockId: e.id });
        }
        this.expectText(e.hay, e.id);
        return 'int';
      }
      case 'substr':
        this.expectText(e.arg, e.id);
        this.expectIndex(e.from, e.id);
        this.expectIndex(e.len, e.id);
        return 'string';
      case 'at': {
        const t = this.env.get(e.name);
        this.expectIndex(e.index, e.id);
        if (e.index2) this.expectIndex(e.index2, e.id);
        if (t === undefined) return 'int';
        if (t === 'string') {
          // One character of a text; a text has no second index.
          if (e.index2)
            this.report({ code: 'wrong-rank', blockId: e.id, params: { name: e.name } });
          return 'char';
        }
        if (!isArray(t)) {
          this.report({ code: 'needs-array', blockId: e.id, params: { name: e.name } });
          return 'int';
        }
        // `a[i]` on a table is a row, which the blocks have no value for: ask for both indexes.
        if (rankOf(t) !== (e.index2 ? 2 : 1)) {
          this.report({ code: 'wrong-rank', blockId: e.id, params: { name: e.name } });
        }
        return elemOf(t);
      }
      case 'size': {
        const t = this.env.get(e.name);
        if (t !== undefined && !isArray(t)) {
          this.report({ code: 'needs-array', blockId: e.id, params: { name: e.name } });
        }
        return 'int';
      }
      case 'missing':
        return 'int';
    }
  }

  private expectIndex(e: Expr, owner: string) {
    const t = this.expr(e);
    if (t !== 'int' && e.k !== 'missing') this.report({ code: 'needs-index', blockId: owner });
  }

  stmt(s: Stmt) {
    switch (s.k) {
      case 'newArray':
        for (const d of s.dims) this.expectIndex(d, s.id);
        this.assign(s.name, arrayOf(s.elem, s.dims.length === 2 ? 2 : 1), s.id);
        break;
      case 'setAt': {
        const t = this.env.get(s.name);
        this.expectIndex(s.index, s.id);
        if (s.index2) this.expectIndex(s.index2, s.id);
        const value = this.expr(s.value);
        if (t === undefined) {
          // The list is made before it is filled; assume it holds what is being put in it.
          this.assign(s.name, arrayOf(scalarOf(value), s.index2 ? 2 : 1), s.id);
        } else if (!isArray(t)) {
          this.report({ code: 'needs-array', blockId: s.id, params: { name: s.name } });
        } else if (rankOf(t) !== (s.index2 ? 2 : 1)) {
          this.report({ code: 'wrong-rank', blockId: s.id, params: { name: s.name } });
        } else if (isNumeric(elemOf(t)) !== isNumeric(value) && value !== 'int') {
          // Putting text into a list of numbers (or the other way round) is a real mistake.
          this.report({
            code: 'type-conflict',
            blockId: s.id,
            params: { name: s.name, was: t, now: value },
          });
        }
        break;
      }
      case 'set':
        this.assign(s.name, this.expr(s.value), s.id);
        break;
      case 'change': {
        const t = this.expectNumeric(s.delta, s.id);
        const cur = this.env.get(s.name);
        if (cur !== undefined && !isNumeric(cur)) {
          this.report({ code: 'needs-number', blockId: s.id });
        } else {
          this.assign(s.name, t, s.id);
        }
        break;
      }
      case 'print':
        this.expr(s.value);
        break;
      case 'if':
        for (const b of s.branches) this.expectBool(b.cond, s.id);
        break;
      case 'repeat':
        this.expectNumeric(s.times, s.id);
        break;
      case 'while':
        this.expectBool(s.cond, s.id);
        break;
      case 'for':
        this.assign(s.name, 'int', s.id);
        this.expectNumeric(s.from, s.id);
        this.expectNumeric(s.to, s.id);
        break;
      case 'break':
        break;
    }
  }

  run(body: readonly Stmt[]) {
    for (const s of walkStmts(body)) this.stmt(s);
  }
}

/** Fills `variables` and `types` and appends type diagnostics. */
export function inferTypes(program: Program): Program {
  const variables: string[] = [];
  const seen = new Set<string>();
  const note = (name: string) => {
    if (!seen.has(name)) {
      seen.add(name);
      variables.push(name);
    }
  };
  for (const s of walkStmts(program.body)) {
    // A list is a variable too: it has to be declared with the others in the C++ view.
    if (
      s.k === 'set' ||
      s.k === 'change' ||
      s.k === 'for' ||
      s.k === 'newArray' ||
      s.k === 'setAt'
    ) {
      note(s.name);
    }
    for (const e of stmtExprs(s)) {
      for (const x of walkExpr(e)) {
        if (x.k === 'var' || x.k === 'at' || x.k === 'size') note(x.name);
      }
    }
  }

  // Types flow through variables, so iterate to a fixpoint before reporting anything.
  const tc = new TypeChecker();
  for (let i = 0; i < 20; i++) {
    const before = JSON.stringify([...tc.env]);
    tc.run(program.body);
    if (JSON.stringify([...tc.env]) === before) break;
  }
  tc.collect = true;
  tc.run(program.body);

  const types: Record<string, ValueType> = {};
  for (const v of variables) types[v] = tc.env.get(v) ?? 'int';
  return { ...program, variables, types, diagnostics: [...program.diagnostics, ...tc.diagnostics] };
}

/** Type of an expression under a program's variable types (used by generators). */
export function typeOf(e: Expr, types: Readonly<Record<string, ValueType>>): ValueType {
  const tc = new TypeChecker();
  for (const [k, v] of Object.entries(types)) tc.env.set(k, v);
  return tc.expr(e);
}
